/**
 * @file hazardCareModule.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Inputs of the wound-type hazard model that come from the logistics side (docs/planning/PLAN_WOUND_HAZARD_IMPL.md
 * section 5): a running tracker of the medical items that decide whether care is in place
 * (antibiotics, dressings), and the derivation of the care object used by woundHazardModule.
 * PURE: the tracker is a plain object created per run; nothing is stored at module level.
 *
 * Antibiotics come from Tab 4: initial stock (logistics.medical_supplies.initial_stock.antibiotics_doses)
 * and push packages / resupply events (their flat `supplies` keys), consumed per day by the same per-triage
 * rates the supply chain module uses (supplyChainModule.calculateDailyConsumption).
 */
import { CARE_FLAG_DERIVATION } from '../../constants/woundHazardParameters.js';
import { assumptionLedger } from '../assumptionLedger.js';

const WHERE = 'hazardCareModule';

/** Map of tracked stock keys (Tab 4 flat names) to the consumption keys of supplyChainModule. */
export const TRACKED_SUPPLY_ITEMS = Object.freeze({
  antibiotics_doses: 'antibiotics',
  dressings_sets: 'dressings_sets'
});

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

/** Planning-default id (constants/planningDefaults.js) used when the stock of an item is not known. */
const UNKNOWN_STOCK_DEFAULT = Object.freeze({
  antibiotics_doses: 'hazard_antibiotics_in_stock',
  dressings_sets: 'hazard_dressings_in_stock'
});

/**
 * @param {Object} p
 * @param {Object} [p.initialStock] logistics.medical_supplies.initial_stock (flat Tab 4 keys). When Tab 4 has no figure
 *   for an item its stock is UNKNOWN: care is assumed in place and the use is listed in the "assumptions used" ledger.
 * @param {Array} [p.resupplyEvents] logistics.medical_supplies.resupply_events ({ day, supplies })
 */
export const createSupplyTracker = ({ initialStock, resupplyEvents = [] }) => {
  if (initialStock !== undefined && initialStock !== null && typeof initialStock !== 'object') {
    throw new Error(`${WHERE}: logistics.medical_supplies.initial_stock must be an object when present`);
  }
  if (!Array.isArray(resupplyEvents)) throw new Error(`${WHERE}: resupplyEvents must be an array`);
  const stock = {};
  const unknownKeys = [];
  Object.keys(TRACKED_SUPPLY_ITEMS).forEach((key) => {
    const known = initialStock && typeof initialStock[key] === 'number' && !Number.isNaN(initialStock[key]);
    if (!known) unknownKeys.push(key);
    stock[key] = known ? initialStock[key] : 0;
  });
  return {
    unknownKeys,
    stock,
    /** Add the resupply events that arrive on `day`. */
    startDay(day) {
      resupplyEvents.filter((e) => e && e.day === day).forEach((e) => {
        Object.keys(TRACKED_SUPPLY_ITEMS).forEach((key) => { stock[key] += num(e.supplies?.[key]); });
      });
    },
    inStock(key) {
      if (!Object.prototype.hasOwnProperty.call(TRACKED_SUPPLY_ITEMS, key)) throw new Error(`${WHERE}: '${key}' is not tracked`);
      if (unknownKeys.includes(key)) {
        // Unknown stock is not zero stock: the planner has not said. Assume care in place and say so.
        assumptionLedger.note(UNKNOWN_STOCK_DEFAULT[key]);
        return true;
      }
      return stock[key] > 0;
    },
    /** Subtract a day's consumption (keys as returned by supplyChainModule.calculateDailyConsumption). */
    consume(consumption) {
      Object.entries(TRACKED_SUPPLY_ITEMS).forEach(([key, consumptionKey]) => {
        stock[key] = Math.max(0, stock[key] - num(consumption?.[consumptionKey]));
      });
    }
  };
};

/**
 * Care object for woundHazardModule.
 * @param {Object} p
 * @param {number} p.controlTimeMin minutes to first intervention (survivalModule: time to first intervention)
 * @param {number} p.trainingScale tourniquet success of this force's first-aid training relative to STANDARD
 * @param {boolean} p.resuscitationInPlace blood supply is not in shortage
 * @param {boolean} p.antibioticsInStock @param {boolean} p.dressingsInStock
 */
export const deriveHazardCare = ({ controlTimeMin, trainingScale, resuscitationInPlace, antibioticsInStock, dressingsInStock }) => {
  if (typeof controlTimeMin !== 'number' || Number.isNaN(controlTimeMin) || controlTimeMin < 0) {
    throw new Error(`${WHERE}: controlTimeMin must be a number of minutes >= 0 (got: ${controlTimeMin})`);
  }
  if (typeof trainingScale !== 'number' || Number.isNaN(trainingScale) || trainingScale <= 0) {
    throw new Error(`${WHERE}: trainingScale must be a number > 0 (got: ${trainingScale})`);
  }
  [['resuscitationInPlace', resuscitationInPlace], ['antibioticsInStock', antibioticsInStock], ['dressingsInStock', dressingsInStock]]
    .forEach(([name, v]) => { if (typeof v !== 'boolean') throw new Error(`${WHERE}: ${name} must be true or false (got: ${v})`); });
  return {
    controlTimeMin,
    trainingScale,
    flags: {
      resuscitation: resuscitationInPlace,
      antibiotics: antibioticsInStock,
      dressing: dressingsInStock,
      fieldCare: CARE_FLAG_DERIVATION.fieldCare(resuscitationInPlace, antibioticsInStock, dressingsInStock),
      burnCare: CARE_FLAG_DERIVATION.burnCare(resuscitationInPlace, antibioticsInStock, dressingsInStock)
    }
  };
};
